import "./user-mobility-readiness.css";

// Short Report: User vs User - Mobility Readiness
// Unique classnames prefixed with tt-umr- to avoid conflicts
const UserMobilityReadinessReport = (comparisonResult) => {
  console.log("UserMobilityReadinessReport data:", comparisonResult);
  const sr = comparisonResult?.comparisonData
    ?.userMobilityReadinessShortReport ||
    comparisonResult?.userMobilityReadinessShortReport || {
      userA: {
        name: "Alice",
        top_matches: [
          {
            role: "Data Analyst",
            mobility_score: 82,
            strengths: [
              "SQL",
              "Data Visualization",
              "Aptitude: Analytical Reasoning",
              "Collaboration",
            ],
            weaknesses: ["Cloud Computing"],
            degree_gap: [],
            major_gap: [],
            job_experience_gap: 0,
          },
          {
            role: "Business Intelligence Specialist",
            mobility_score: 82,
            strengths: ["SQL", "Dashboarding", "Aptitude: Pattern Recognition"],
            weaknesses: ["Data Storytelling"],
            degree_gap: [],
            major_gap: [],
            job_experience_gap: 1,
          },
        ],
      },
      userB: {
        name: "Brian",
        top_matches: [
          {
            role: "Data Engineer",
            mobility_score: 74,
            strengths: ["Python", "ETL", "Aptitude: Problem Solving"],
            weaknesses: ["Data Visualization", "Stakeholder Communication"],
            degree_gap: ["Bachelor's Degree"],
            major_gap: ["Computer Science"],
            job_experience_gap: 2,
          },
        ],
      },
      overview:
        "Alice’s maximum mobility score is 82 across Data Analyst and BI Specialist, indicating strong near-term mobility with minimal experience gap. Brian’s top mobility score is 74 for Data Engineer, with a 2-year experience gap plus degree/major gaps. Overall, Alice appears more readily deployable; Brian would benefit from targeted development in data visualization, stakeholder communication, and closing experience/education gaps.",
    };

  const pickMaxMobility = (user) => {
    const list = Array.isArray(user?.top_matches) ? user.top_matches : [];
    if (list.length === 0)
      return {
        score: 0,
        experienceGap: 0,
        strengths: [],
        weaknesses: [],
        degreeGap: [],
        majorGap: [],
      };

    // Find max mobility_score
    const maxScore = Math.max(
      ...list.map((m) => Number(m?.mobility_score ?? 0))
    );
    const tied = list.filter(
      (m) => Number(m?.mobility_score ?? 0) === maxScore
    );

    // Combine attributes across ties (unique values)
    const uniq = (arr) => Array.from(new Set(arr.filter(Boolean)));

    const strengths = uniq(
      tied.flatMap((m) => (Array.isArray(m?.strengths) ? m.strengths : []))
    );
    const weaknesses = uniq(
      tied.flatMap((m) => (Array.isArray(m?.weaknesses) ? m.weaknesses : []))
    );
    const degreeGap = uniq(
      tied.flatMap((m) => (Array.isArray(m?.degree_gap) ? m.degree_gap : []))
    );
    const majorGap = uniq(
      tied.flatMap((m) => (Array.isArray(m?.major_gap) ? m.major_gap : []))
    );
    // Choose the minimum positive experience gap among ties (closest readiness)
    const expGaps = tied.map((m) => Number(m?.job_experience_gap ?? 0));
    const experienceGap = Math.max(0, Math.min(...expGaps));

    return {
      score: maxScore,
      experienceGap,
      strengths,
      weaknesses,
      degreeGap,
      majorGap,
    };
  };

  const personAName = sr?.userA?.name ?? "Person A";
  const personBName = sr?.userB?.name ?? "Person B";

  const a = pickMaxMobility(sr?.userA);
  const b = pickMaxMobility(sr?.userB);

  return (
    <div className="tt-umr-short-report-container">
      <div className="tt-umr-report-header">
        <h3>Mobility Readiness</h3>
        <p>
          Quick overview of internal movement potential between the selected
          users
        </p>
      </div>

      {/* Metric Cards */}
      <div className="tt-umr-comparison-summary">
        <div className="tt-umr-summary-card">
          <div className="tt-umr-summary-icon" aria-hidden="true">
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
              <circle cx="9" cy="7" r="4" />
            </svg>
          </div>
          <div className="tt-umr-summary-content">
            <div className="tt-umr-summary-label">
              {
                comparisonResult.comparisonData.mobilityShortReport.userAData
                  .name
              }
            </div>
            <div className="tt-umr-summary-value">
              {Math.round(
                comparisonResult.comparisonData.mobilityShortReport.userAData
                  .mobilityScore
              )}
              %
            </div>
            <div className="tt-umr-summary-label">Mobility Score</div>
          </div>
        </div>

        <div className="tt-umr-summary-card">
          <div className="tt-umr-summary-icon" aria-hidden="true">
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
              <circle cx="9" cy="7" r="4" />
            </svg>
          </div>
          <div className="tt-umr-summary-content">
            <div className="tt-umr-summary-label">
              {
                comparisonResult.comparisonData.mobilityShortReport.userBData
                  .name
              }
            </div>
            <div className="tt-umr-summary-value">
              {Math.round(
                comparisonResult.comparisonData.mobilityShortReport.userBData
                  .mobilityScore
              )}
              %
            </div>
            <div className="tt-umr-summary-label">Mobility Score</div>
          </div>
        </div>
      </div>

      {/* Progress Bars */}
      <div className="tt-umr-metrics-comparison">
        <h3>Mobility Score Comparison</h3>
        <div className="tt-umr-metrics-chart">
          <div className="tt-umr-metric-bar-container">
            <div className="tt-umr-metric-info">
              <span className="tt-umr-metric-name">
                {
                  comparisonResult.comparisonData.mobilityShortReport.userAData
                    .name
                }
              </span>
            </div>
            <div className="tt-umr-metric-bar">
              <div
                className="tt-umr-metric-fill"
                style={{
                  width: `${Math.max(
                    0,
                    Math.min(
                      100,
                      Math.round(
                        comparisonResult.comparisonData.mobilityShortReport
                          .userAData.mobilityScore
                      )
                    )
                  )}%`,
                }}
              ></div>
              <span className="tt-umr-metric-value">
                {Math.max(
                  0,
                  Math.min(
                    100,
                    Math.round(
                      comparisonResult.comparisonData.mobilityShortReport
                        .userAData.mobilityScore
                    )
                  )
                )}
                %
              </span>
            </div>
          </div>

          <div className="tt-umr-metric-bar-container">
            <div className="tt-umr-metric-info">
              <span className="tt-umr-metric-name">
                {
                  comparisonResult.comparisonData.mobilityShortReport.userBData
                    .name
                }
              </span>
            </div>
            <div className="tt-umr-metric-bar">
              <div
                className="tt-umr-metric-fill"
                style={{
                  width: `${Math.max(
                    0,
                    Math.min(
                      100,
                      Math.round(
                        comparisonResult.comparisonData.mobilityShortReport
                          .userBData.mobilityScore
                      )
                    )
                  )}%`,
                }}
              ></div>
              <span className="tt-umr-metric-value">
                {Math.max(
                  0,
                  Math.min(
                    100,
                    Math.round(
                      comparisonResult.comparisonData.mobilityShortReport
                        .userBData.mobilityScore
                    )
                  )
                )}
                %
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Analysis Overview */}
      <div className="tt-umr-insights-section">
        <h3>Analysis Overview</h3>
        <div className="tt-umr-analysis-paragraph">
          <p>{comparisonResult.comparisonData.mobilityShortReport.overview}</p>
        </div>
      </div>
    </div>
  );
};

export default UserMobilityReadinessReport;
